// 職責：全應用唯一的 fetch 包裝 —— 組請求、附 token、逾時、把所有失敗正規化成 ApiError

/*
 * 設計出發點（doc 05 §1）：後端沒有全域例外處理、沒有回應信封，production 的 500 是空 body，
 * 404 的 body 由 nginx 決定（可能是 HTML）。因此：
 *   1. 一律先 res.text() 再嘗試 JSON.parse —— 直接 res.json() 會被空 body 與 HTML 錯誤頁炸掉。
 *   2. 所有失敗（含 fetch reject）都轉成 ApiError，應用層只需認得 kind，不必再碰 Response。
 *   3. 每個請求配一個 requestId：後端沒有錯誤碼，這是使用者回報問題時唯一可對應的線索。
 *
 * 本檔不含任何業務邏輯（doc 06 §5.6）：路徑慣例、整列合併、Zod 解析都在 defineResource。
 */

export type ApiErrorKind =
  | 'network' // 斷線 / DNS / 服務未啟動
  | 'timeout' // 逾時
  | 'unauthorized' // 401
  | 'forbidden' // 403
  | 'notfound' // 404
  | 'method' // 405 —— 呼叫方式錯誤，屬開發期 bug
  | 'validation' // 400 RFC7807，帶欄位錯誤
  | 'conflict' // 409
  | 'server' // 500，通常無 body
  | 'parse' // 通過 HTTP 但不符 Zod schema（由 defineResource 拋出）

export interface ApiErrorInit {
  kind: ApiErrorKind
  endpoint: string
  requestId: string
  message?: string
  status?: number
  fieldErrors?: Record<string, string[]>
  /** 後端業務錯誤碼（body.code），例如 `amount_mismatch`；沒有就是 undefined */
  code?: string
  /** 後端業務錯誤說明（body.msg），見 `ApiError.serverMessage` */
  serverMessage?: string
}

/** 各 kind 的預設訊息。面向使用者的正式文案在 UI 層（doc 05 §3.1），這裡只保證 message 永遠可讀 */
const DEFAULT_MESSAGE: Record<ApiErrorKind, string> = {
  network: '連線失敗，請檢查網路後重試',
  timeout: '操作逾時，請重試',
  unauthorized: '登入已逾時，請重新登入',
  forbidden: '你沒有權限執行此操作',
  notfound: '找不到資料，可能已被刪除',
  method: '操作失敗（E-405）',
  validation: '輸入內容有誤，請修正後重試',
  conflict: '資料已被他人修改，請重新載入',
  server: '操作失敗，請稍後再試',
  parse: '資料格式異常，請聯絡技術支援',
}

export class ApiError extends Error {
  readonly kind: ApiErrorKind
  readonly status?: number
  readonly fieldErrors?: Record<string, string[]>
  readonly requestId: string
  readonly endpoint: string
  /** 後端業務錯誤碼（webapi 慣用的 `{ code, msg }` 形狀中的 `code`） */
  readonly code?: string
  /**
   * 後端業務錯誤說明（`{ code, msg }` 中的 `msg`）。
   *
   * ⚠️ 刻意**不**寫入 `message`：webapi 各端點的 `msg` 用語不一（有粵語口語、有簡體），
   *    一律顯示會令全站錯誤文案變得參差。只有確定該端點的 `msg` 適合直接顯示的呼叫端
   *    （例如 `api/deptSplitHis`）才自行選用；其餘頁面維持 `DEFAULT_MESSAGE`。
   */
  readonly serverMessage?: string

  constructor(init: ApiErrorInit) {
    super(init.message ?? DEFAULT_MESSAGE[init.kind])
    this.name = 'ApiError'
    this.kind = init.kind
    this.status = init.status
    this.fieldErrors = init.fieldErrors
    this.requestId = init.requestId
    this.endpoint = init.endpoint
    this.code = init.code
    this.serverMessage = init.serverMessage
  }
}

export interface RequestOptions {
  /** 門店業務基址（含結尾斜線），來自 session store 選定的門店 */
  baseUrl: string
  token?: string | null
  timeoutMs?: number
  /** 呼叫端自己的取消訊號（元件卸載、Query 取消） */
  signal?: AbortSignal
  /**
   * 額外請求 header。
   * ★ 目前唯一用途是 `FileUpload/files/{category}`：後端把 goods_id / merchant_id /
   *   default_display 三個關鍵值讀自 **header**（`id` / `mid` / `did`，
   *   FileUploadController.cs:205-207），不是 body。漏送不會報錯，
   *   `Convert.ToInt32(null)` = 0 → 寫出一行 goods_id=0 的孤兒圖片列
   *   （元朗店真庫內就有 9 行這樣來的垃圾）。
   */
  extraHeaders?: Record<string, string>
  /**
   * 停用自動重試。
   * ⚠️ `GET remove/{id}`（merchant_table / merchant_package / merchant_task / shop_config）
   * 是有副作用的 GET（doc 05 §5.2），重試等於重複刪除，必須帶此旗標。
   */
  noRetry?: boolean
}

/** 一般讀寫的逾時；上傳 60 秒、測試列印 10 秒由呼叫端覆寫（doc 05 §4.1） */
const DEFAULT_TIMEOUT_MS = 15_000
/** 只有 GET 會自動重試，且僅限暫時性失敗；上限 2 次（doc 05 §5.1） */
const MAX_RETRIES = 2
const RETRY_BASE_DELAY_MS = 300
const RETRIABLE_KINDS: ReadonlySet<ApiErrorKind> = new Set<ApiErrorKind>([
  'network',
  'timeout',
  'server',
])

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE'

/** 6 位 base36 追蹤碼。這不是錯誤碼，只用於把使用者回報對應到 log（doc 05 §6） */
function createRequestId(): string {
  return Math.random().toString(36).slice(2, 8).padEnd(6, '0')
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}

/* ────────────────────────────── 日期時間上線格式 ────────────────────────────── */

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * `Date` → `YYYY-MM-DDTHH:mm:ss`（**本地時區**、沒有 `Z` 沒有 offset）。
 * 匯出只是為了給 spec 直接驗證；正常路徑經 `serializeBody`。
 */
export function toLocalWireDate(value: Date): string {
  return (
    `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}` +
    `T${pad2(value.getHours())}:${pad2(value.getMinutes())}:${pad2(value.getSeconds())}`
  )
}

/**
 * 🚨🚨 **不可以用 `JSON.stringify` 的預設行為送 `Date`。**
 *
 * 預設會叫 `Date.prototype.toJSON()` → `toISOString()` → **UTC 兼帶 `Z`**。
 * 香港 UTC+8，所以「06:00 開店」送出去是 `2022-12-27T22:00:00.000Z`。接收那邊：
 *
 *   1. `WebApiService/Startup.cs:85` `AddNewtonsoftJson` **一個設定都沒有加**
 *      → 預設 `DateTimeZoneHandling.RoundtripKind`：見到 `Z` 就當 `Kind=Utc`，
 *        **不會**轉回本地。DateTime 就是 22:00。
 *   2. `AM.BOCoreLibrary/Extend/BOExtensions.cs:44`
 *      `AppContext.SetSwitch("Npgsql.EnableLegacyTimestampBehavior", true)`
 *      → 那個 22:00 原封不動寫入 `timestamp without time zone`。
 *
 * 結果：**寫入早了 8 個鐘**，日期還會落回前一日。2026-09-03 在 143 UAT 場實測過：
 * 送 `2022-12-29T22:00:00.000Z` → 庫內 `2022-12-29 22:00:00`（本意是 12-30 06:00）。
 *
 * 讀取方向是正確的：PG `timestamp without time zone` → `Kind=Unspecified` → Newtonsoft
 * 輸出 `"2022-12-28T07:00:00"`（沒有後綴）→ `new Date(...)` 當本地。
 * 即是**單邊偏差**，所以症狀是「改完之後差了 8 個鐘」而不是一直都差。
 *
 * 🔴 對 `merchant_user.opening_time` / `closing_time` 尤其毒：開鋪退到前一日、
 *    收店留在當日 → **日期差由 0 變 1** → 清機當它是跨夜店
 *    （`merchant_orderController.cs:9010` 的 `bizShift`）→ 全日訂單的 `his_time`
 *    歸入前一個營業日 → 後台報表當日變零。改營業時間去解跨夜問題，反而會再造一次。
 *
 * ⚠️ replacer 收到的 `value` **已經是** `toJSON()` 之後那個字串（規範次序：先 `toJSON`
 *    再 replacer），所以一定要用 `function` 不用箭頭函式，經 `this[key]` 取回原本的 `Date`。
 */
function localDateReplacer(this: unknown, key: string, value: unknown): unknown {
  const raw = (this as Record<string, unknown>)[key]
  if (!(raw instanceof Date)) return value
  // Invalid Date 保持與 `toJSON()` 一樣回 null，不要寫一個 `NaN-NaN-NaN` 入庫
  return Number.isNaN(raw.getTime()) ? null : toLocalWireDate(raw)
}

/** POST body → JSON 字串。所有 `Date` 一律出本地時間格式（見 `localDateReplacer`）。 */
export function serializeBody(body: unknown): string {
  return JSON.stringify(body ?? null, localDateReplacer)
}

/**
 * 開發模式的請求日誌。
 * ⚠️ 絕不輸出 request/response body：舊版 auth.js 曾 console.log 密碼與 recovery code（缺陷 #33）。
 * production 完全不輸出。
 */
function logDev(
  requestId: string,
  method: HttpMethod,
  path: string,
  status: number | string,
): void {
  if (!import.meta.env.DEV) return
  // eslint-disable-next-line no-console -- 僅開發模式、僅輸出方法/路徑/狀態，不含任何 body
  console.debug(`[${requestId}] ${method} ${path} → ${status}`)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * 從 RFC7807 的 `errors` 取出逐欄錯誤。
 * 後端只有 [ApiController] 的模型繫結驗證會產生這個形狀（doc 02 §1.8），
 * 其餘 400 可能什麼都沒有，因此每一層都要檢查而不是假設。
 */
function extractFieldErrors(body: unknown): Record<string, string[]> | undefined {
  if (!isRecord(body) || !isRecord(body.errors)) return undefined
  const result: Record<string, string[]> = {}
  for (const [field, messages] of Object.entries(body.errors)) {
    if (Array.isArray(messages)) {
      const texts = messages.filter((m): m is string => typeof m === 'string')
      if (texts.length > 0) result[field] = texts
    } else if (typeof messages === 'string') {
      result[field] = [messages]
    }
  }
  return Object.keys(result).length > 0 ? result : undefined
}

/* ─────────────────── 「token 被後端拒絕」廣播（doc 09 §3.5）─────────────────── */

type TokenRejectedHook = (endpoint: string) => void
const tokenRejectedHooks = new Set<TokenRejectedHook>()

/**
 * 登記一個「帶著 token 但被後端回 401」的處理器（回傳一個解除登記的函式）。
 *
 * ★ 為什麼放在 http 層而不是 TanStack Query 的 onError：登入、上載這些路徑不經 Query，
 *   而這個訊號恰恰在那幾條路徑才出現得最多。這裡是**唯一**每個請求都會經過的窄口。
 */
export function registerTokenRejectedHook(hook: TokenRejectedHook): () => void {
  tokenRejectedHooks.add(hook)
  return () => {
    tokenRejectedHooks.delete(hook)
  }
}

function notifyTokenRejected(endpoint: string): void {
  for (const hook of tokenRejectedHooks) {
    try {
      hook(endpoint)
    } catch {
      // 一個處理器出錯不可以連累請求本身的錯誤處理
    }
  }
}

function statusToKind(status: number): ApiErrorKind {
  switch (status) {
    case 400:
      return 'validation'
    case 401:
      return 'unauthorized'
    case 403:
      return 'forbidden'
    case 404:
      return 'notfound'
    case 405:
      return 'method'
    case 409:
      return 'conflict'
    default:
      // 其餘一律當伺服器端失敗；500 的 body 是空的，沒有更多資訊可挖（doc 02 §1.8）
      return 'server'
  }
}

/** 取 webapi `{ code, msg }` 形狀中的某個字串欄位；不是非空字串就回 undefined */
function extractStringField(body: unknown, field: 'code' | 'msg'): string | undefined {
  if (!isRecord(body)) return undefined
  const value = body[field]
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

/** 只取 body 中「安全可顯示」的訊息欄位；沒有就回 undefined，讓 ApiError 用預設文案 */
function extractMessage(body: unknown): string | undefined {
  if (!isRecord(body)) return undefined
  const candidate = body.title ?? body.message ?? body.error
  return typeof candidate === 'string' && candidate.trim() !== '' ? candidate : undefined
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms)
    // 呼叫端取消時立刻結束等待，避免元件已卸載仍佔著計時器
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        resolve()
      },
      { once: true },
    )
  })
}

async function attempt(
  method: HttpMethod,
  path: string,
  body: unknown,
  opts: RequestOptions,
  requestId: string,
): Promise<unknown> {
  const endpoint = `${method} ${path}`
  const url = joinUrl(opts.baseUrl, path)

  const controller = new AbortController()
  // 用旗標區分「我們的逾時」與「呼叫端主動取消」：AbortError 本身分不出來源
  let timedOut = false
  const timer = setTimeout(() => {
    timedOut = true
    controller.abort()
  }, opts.timeoutMs ?? DEFAULT_TIMEOUT_MS)
  const onCallerAbort = (): void => controller.abort()
  opts.signal?.addEventListener('abort', onCallerAbort, { once: true })

  const headers: Record<string, string> = { Accept: 'application/json' }
  // 目前後端只有 users 與 FileUpload 套 [Authorize]（doc 02 §1.7），
  // 仍一律附上：日後補齊認證時前端不需再改一行。
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`
  const hasBody = method === 'POST'
  // FormData 要留給瀏覽器自己設定 Content-Type —— multipart 的 boundary 只有它知道，
  // 我們硬寫一個 'multipart/form-data' 就會沒有 boundary，後端 parse 不到（400）。
  const isFormBody = hasBody && typeof FormData !== 'undefined' && body instanceof FormData
  if (hasBody && !isFormBody) headers['Content-Type'] = 'application/json'
  Object.assign(headers, opts.extraHeaders)

  let res: Response
  try {
    res = await fetch(url, {
      method,
      headers,
      // POST 即使沒有 body 也送 'null'：後端 action 多半宣告了參數，缺 body 會變成 400
      body: hasBody ? (isFormBody ? (body as FormData) : serializeBody(body)) : undefined,
      signal: controller.signal,
      // CORS 不允許 credentials（doc 02 §1.9），送 cookie 只會讓請求被瀏覽器擋下
      credentials: 'omit',
    })
  } catch (error) {
    if (opts.signal?.aborted && !timedOut) {
      // 呼叫端主動取消不是錯誤，原樣拋出讓 TanStack Query 辨識為 cancelled
      throw error
    }
    logDev(requestId, method, path, timedOut ? 'timeout' : 'network')
    throw new ApiError({ kind: timedOut ? 'timeout' : 'network', endpoint, requestId })
  } finally {
    clearTimeout(timer)
    opts.signal?.removeEventListener('abort', onCallerAbort)
  }

  logDev(requestId, method, path, res.status)

  // 先讀純文字：204 是空字串、nginx 的 502 是 HTML，兩者都會讓 res.json() 直接拋錯
  const text = await res.text()
  let parsed: unknown = undefined
  let parseFailed = false
  if (text.trim() !== '') {
    try {
      parsed = JSON.parse(text)
    } catch {
      parseFailed = true
    }
  }

  if (!res.ok) {
    const kind = statusToKind(res.status)
    /*
     * 帶著 token 仍收到 401 ＝ **該 token 後端不認**（過期／不是這間店那張／帳號停用）。
     * 一定要廣播出來：門店 webapi 只有 `users` 與 `FileUpload` 兩支套了 `[Authorize]`，
     * 其餘業務端點一律不驗 —— 所以該 app 可以在一個已失效的 session 運行足一日，
     * 直到有人按上載相片才見到一次 401（2026-09-18 浸會 801 就是這樣）。
     * 沒有 token 的那些 401（例如登入本身打錯密碼）不算，由呼叫端自己處理。
     */
    if (res.status === 401 && typeof opts.token === 'string' && opts.token !== '') {
      notifyTokenRejected(endpoint)
    }
    throw new ApiError({
      kind,
      endpoint,
      requestId,
      status: res.status,
      message: extractMessage(parsed),
      fieldErrors: kind === 'validation' ? extractFieldErrors(parsed) : undefined,
      code: extractStringField(parsed, 'code'),
      serverMessage: extractStringField(parsed, 'msg'),
    })
  }

  if (parseFailed) {
    // 200 但不是 JSON：多半是被反向代理攔截後回了登入頁或錯誤頁
    throw new ApiError({ kind: 'parse', endpoint, requestId, status: res.status })
  }

  // 空 body（如 DELETE 的 204）回 null，讓上層的 Zod 明確判定，而不是讓 undefined 流進 UI
  return parsed ?? null
}

async function request(
  method: HttpMethod,
  path: string,
  body: unknown,
  opts: RequestOptions,
): Promise<unknown> {
  const requestId = createRequestId()
  // 寫入類一律不重試：後端無 Idempotency-Key，重送 update/0 會產生重複資料（doc 05 §5.1）
  const canRetry = method === 'GET' && !opts.noRetry

  for (let attemptNo = 0; ; attemptNo++) {
    try {
      return await attempt(method, path, body, opts, requestId)
    } catch (error) {
      const retriable =
        canRetry &&
        attemptNo < MAX_RETRIES &&
        error instanceof ApiError &&
        RETRIABLE_KINDS.has(error.kind) &&
        !opts.signal?.aborted
      if (!retriable) throw error
      await delay(RETRY_BASE_DELAY_MS * 2 ** attemptNo, opts.signal)
    }
  }
}

export const http = {
  get(path: string, opts: RequestOptions): Promise<unknown> {
    return request('GET', path, undefined, opts)
  },
  post(path: string, body: unknown, opts: RequestOptions): Promise<unknown> {
    return request('POST', path, body, opts)
  },
  /** multipart 上傳。與 `post` 共用逾時／重試禁令／ApiError 正規化，只是不做 JSON 編碼 */
  postForm(path: string, form: FormData, opts: RequestOptions): Promise<unknown> {
    return request('POST', path, form, opts)
  },
  /**
   * 只給 `defineResource` 的 `upsertStyle: 'rest'` 用（merchant_setmeal_item 一類
   * 沒有 `POST update/{id}` 的散件 controller）。與 POST 同樣不重試（doc 05 §5.1）。
   */
  put(path: string, body: unknown, opts: RequestOptions): Promise<unknown> {
    return request('PUT', path, body, opts)
  },
  del(path: string, opts: RequestOptions): Promise<unknown> {
    return request('DELETE', path, undefined, opts)
  },
}
